import React, { useContext, useEffect, useState } from 'react';
import cx from 'clsx';
import { Divider } from '@mui/material';
import Menu from 'components/Menu/Menu';
import { RoomLayoutValuesContext } from 'features/layout/Context';
import { useCustomTilePinControl } from 'features/streaming/components/controls/useCustomTilePinControl';
import { StreamControlButton } from 'features/streaming/components/StreamControlButton';
import { StreamControlMenuItem } from 'features/streaming/components/StreamControlMenuItem';
import { useCustomTileExpandControl } from 'features/streaming/components/controls/useCustomTileExpandContro';
import { CustomTileData } from 'features/layout/features/config/types';
import { customControlTriggered } from 'features/layout/actions';
import { selectCustomControls } from 'features/layout/features/config/configSlice';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { formatCustomTileToSource } from 'utils/custom-tile/format';

export const CustomTileControls = ({
  data,
  collapse,
}: {
  data: CustomTileData;
  collapse: boolean;
}) => {
  const dispatch = useAppDispatch();
  const [menuOpen, setMenuOpen] = useState(false);

  const { roomWidth, roomHeight } = useContext(RoomLayoutValuesContext);
  const customControls = useAppSelector((state) => selectCustomControls(state, 'custom'));
  const closeMenu = () => setMenuOpen(false);

  const { show: showPin, ...pinControl } = useCustomTilePinControl(data, closeMenu);
  const { show: showExpand, ...expandControl } = useCustomTileExpandControl(data, closeMenu);

  const controls: any[] = [];
  const Control = collapse ? StreamControlMenuItem : StreamControlButton;

  if (showPin) {
    controls.push({ key: 'pin', ...pinControl });
  }

  if (showExpand) {
    controls.push({ key: 'fullscreen', ...expandControl });
  }

  if (collapse && controls.length && Object.values(customControls).length) {
    controls.push('divider');
  }

  // eslint-disable-next-line guard-for-in
  for (const controlName in customControls) {
    const control = customControls[controlName];

    controls.push({
      key: `customAction-${controlName}`,
      onClick: () =>
        dispatch(
          customControlTriggered({
            action: controlName,
            source: formatCustomTileToSource(data),
          })
        ),
      icon: control.properties.icon || '',
      text: control.properties.label,
    });
  }

  const haveControls = controls.length;

  useEffect(() => {
    closeMenu();
  }, [roomWidth, roomHeight]);

  const renderControls = () =>
    controls.map((config) => {
      if (config === 'divider') {
        return <Divider />;
      }
      const { key, ...rest } = config;

      return <Control key={key} {...rest} />;
    });

  const renderCollapsedMenu = () =>
    haveControls ? (
      <Menu
        id={`tile-controls-${data.name}`}
        open={menuOpen}
        onClose={closeMenu}
        placement="top-start"
        trigger={
          <StreamControlButton
            icon="settings1"
            onClick={() => setMenuOpen((open) => !open)}
            className={cx({ 'hover-child': !menuOpen })}
          />
        }
        PaperProps={{
          sx: (theme) => ({
            backgroundColor: theme.palette.grey['900'],
          }),
        }}
        sx={(theme) => ({
          color: theme.palette.common.white,
        })}
      >
        {renderControls()}
      </Menu>
    ) : null;

  return collapse ? renderCollapsedMenu() : renderControls();
};
